import React, { useState } from "react";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
// material
import {
  Box,
  Button,
  Container,
  Checkbox,
  FormControl,
  FormGroup,
  FormControlLabel,
  Grid,
  MenuItem,
  Typography,
  TextField,
  Stack
} from "@mui/material";

// components
import Page from "../../components/Page";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import CircleUnchecked from "@material-ui/icons/RadioButtonUnchecked";
import CircleCheckedFilled from "@material-ui/icons/CheckCircle";
import { researchExtensionPostDocUpload } from "../../core/service/researchExtension.service";
import Swal from "sweetalert2";

// ----------------------------------------------------------------------
// const ROLE_ADMIN = "admin";
// const ROLE_MENTOR = "mentor";
// const ROLE_STD = "std";

const howLongExtensionPeriodOption = [
  {
    value: "1month",
    label: "1 month"
  },
  {
    value: "2month",
    label: "2 month"
  },
  {
    value: "3month",
    label: "3 month"
  },
  {
    value: "4month",
    label: "4 month"
  },
  {
    value: "5month",
    label: "5 month"
  },
  {
    value: "6month",
    label: "6 month"
  }
];

export default function RequestExtensionPostDoc() {
  const navigate = useNavigate();
  const { user: currentUser } = useSelector((state) => state.authReducer);

  const [followingResearchPaperSubmitted, setFollowingResearchPaperSubmitted] =
    useState(false);
  const [manuscriptAvailable, setManuscriptAvailable] = useState(false);
  const [paperUnSubmitted, setPaperUnSubmitted] = useState(false);
  const [othersPleaseMention, setOthersPleaseMention] = useState(true);
  const [disableSubmit, setDisableSubmit] = useState(true);

  const isValidDate = (d) => {
    return d instanceof Date && !isNaN(d);
  };

  const RequestExtensionPostDocSchema = Yup.object().shape({
    receiver_scholarship_date: Yup.object().shape({
      startDate: Yup.string().nullable().required(`Date is required`),
      endDate: Yup.string().nullable().required(`Date is required`)
    }),
    extensions_period: Yup.string().required(`is required`),
    had_asked_extensions_period_date: Yup.object().shape({
      startDate: Yup.string().nullable(),
      endDate: Yup.string().nullable()
    }),
    how_long_extension_period: Yup.string().required(`is required`),
    what_time_extend_period: Yup.object().shape({
      startDate: Yup.string().nullable().required(`Date is required`),
      endDate: Yup.string().nullable().required(`Date is required`)
    }),
    date_submission:
      followingResearchPaperSubmitted &&
      Yup.string().nullable().required(`Date is required`),
    manuscript_available:
      manuscriptAvailable &&
      Yup.object()
        .shape({
          file: Yup.mixed()
        })
        .nullable()
        .required(`File is required`),
    mention_details: paperUnSubmitted && Yup.string().required(`is required`),
    others_please_mention:
      !othersPleaseMention && Yup.string().required(`is required`)
  });

  const formik = useFormik({
    initialValues: {
      receiver_scholarship_date: {
        startDate: "",
        endDate: ""
      },
      extensions_period: "",
      had_asked_extensions_period_date: {
        startDate: "",
        endDate: ""
      },
      how_long_extension_period: "",
      what_time_extend_period: {
        startDate: "",
        endDate: ""
      },
      date_submission: null,
      manuscript_available: {
        file: {}
      },
      mention_details: "",
      others_please_mention: ""
    },
    validationSchema: RequestExtensionPostDocSchema,
    onSubmit: async (values) => {
      let data_form = new FormData();

      data_form.append("user_id", currentUser.id);
      data_form.append(
        "receiver_scholarship_start_date",
        values.receiver_scholarship_date.startDate
      );
      data_form.append(
        "receiver_scholarship_end_date",
        values.receiver_scholarship_date.endDate
      );
      data_form.append("extensions_period", values.extensions_period);
      data_form.append(
        "had_asked_extensions_period_start_date",
        values.had_asked_extensions_period_date.startDate
      );
      data_form.append(
        "had_asked_extensions_period_end_date",
        values.had_asked_extensions_period_date.endDate
      );
      data_form.append(
        "how_long_extension_period",
        values.how_long_extension_period
      );
      data_form.append(
        "what_time_extend_period_start_date",
        values.what_time_extend_period.startDate
      );
      data_form.append(
        "what_time_extend_period_end_date",
        values.what_time_extend_period.endDate
      );
      data_form.append("date_submission", values.date_submission);
      data_form.append(
        "manuscript_available",
        values.manuscript_available.file
      );
      data_form.append("mention_details", values.mention_details);
      data_form.append("others_please_mention", values.others_please_mention);

      await researchExtensionPostDocUpload(data_form).then(() => {
        Swal.fire({
          icon: "success",
          title: "Successfully",
          showConfirmButton: false,
          timer: 1500
        }).then((result) =>
          navigate(`/extension-std/post_doc`, { replace: true })
        );
      });
    }
  });

  const {
    errors,
    getFieldProps,
    handleSubmit,
    handleChange,
    isSubmitting,
    touched,
    values,
    setFieldValue
  } = formik;

  return (
    <Page title="Request Extension">
      <Container maxWidth="xl">
        <Box sx={{ pb: 5 }}>
          <Typography variant="h4">Research Period Extension</Typography>
        </Box>

        <Grid
          container
          direction="column"
          justifyContent="space-around"
          alignItems="center"
          spacing={3}
          sx={{ paddingTop: 3 }}
        >
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Stack spacing={4}>
                <FormControl fullWidth>
                  <Typography variant="subtitle1">
                    This part is necessary for those who send last semester Part
                    2C.
                  </Typography>

                  <Typography variant="subtitle2" sx={{ pt: 4 }}>
                    Semester you start receiving scholarship *
                  </Typography>
                  <Stack direction="row" spacing={3}>
                    <FormControl fullWidth>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          inputFormat="dd/MM/yyyy"
                          value={values.receiver_scholarship_date.startDate}
                          onChange={(date) => {
                            if (isValidDate(date)) {
                              setFieldValue(
                                "receiver_scholarship_date.startDate",
                                date.toISOString()
                              );
                            } else {
                              setFieldValue(
                                "receiver_scholarship_date.startDate",
                                date
                              );
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={Boolean(
                                touched.receiver_scholarship_date?.startDate &&
                                  errors.receiver_scholarship_date?.startDate
                              )}
                              helperText={
                                touched.receiver_scholarship_date?.startDate &&
                                errors.receiver_scholarship_date?.startDate
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </FormControl>

                    <Typography sx={{ paddingTop: 2 }}>to</Typography>

                    <FormControl fullWidth>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          inputFormat="dd/MM/yyyy"
                          value={values.receiver_scholarship_date.endDate}
                          onChange={(date) => {
                            if (isValidDate(date)) {
                              setFieldValue(
                                "receiver_scholarship_date.endDate",
                                date.toISOString()
                              );
                            } else {
                              setFieldValue(
                                "receiver_scholarship_date.endDate",
                                date
                              );
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={Boolean(
                                touched.receiver_scholarship_date?.endDate &&
                                  errors.receiver_scholarship_date?.endDate
                              )}
                              helperText={
                                touched.receiver_scholarship_date?.endDate &&
                                errors.receiver_scholarship_date?.endDate
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </FormControl>
                  </Stack>
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    Have you ever applied for a research period extension? *
                  </Typography>
                  <TextField
                    select
                    value={values.extensions_period}
                    onChange={(e) => handleChange(e)}
                    {...getFieldProps("extensions_period")}
                    error={Boolean(
                      touched.extensions_period && errors.extensions_period
                    )}
                    helperText={
                      touched.extensions_period && errors.extensions_period
                    }
                  >
                    <MenuItem value={"No, I have not. This is the first time"}>
                      No, I have not. This is the first time
                    </MenuItem>
                    <MenuItem value={"Yes, I have. This is the second time."}>
                      Yes, I have. This is the second time.
                    </MenuItem>
                    <MenuItem value={"Yes, I have. This is the third time"}>
                      Yes, I have. This is the third time
                    </MenuItem>
                  </TextField>
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    If you had applied for a research period extension, please
                    specify
                  </Typography>
                  <Stack direction="row" spacing={3}>
                    <FormControl fullWidth>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          inputFormat="dd/MM/yyyy"
                          value={
                            values.had_asked_extensions_period_date.startDate
                          }
                          onChange={(date) => {
                            if (isValidDate(date)) {
                              setFieldValue(
                                "had_asked_extensions_period_date.startDate",
                                date.toISOString()
                              );
                            } else {
                              setFieldValue(
                                "had_asked_extensions_period_date.startDate",
                                date
                              );
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={Boolean(
                                touched.had_asked_extensions_period_date
                                  ?.startDate &&
                                  errors.had_asked_extensions_period_date
                                    ?.startDate
                              )}
                              helperText={
                                touched.had_asked_extensions_period_date
                                  ?.startDate &&
                                errors.had_asked_extensions_period_date
                                  ?.startDate
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </FormControl>

                    <Typography sx={{ paddingTop: 2 }}>to</Typography>

                    <FormControl fullWidth>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          inputFormat="dd/MM/yyyy"
                          value={
                            values.had_asked_extensions_period_date.endDate
                          }
                          onChange={(date) => {
                            if (isValidDate(date)) {
                              setFieldValue(
                                "had_asked_extensions_period_date.endDate",
                                date.toISOString()
                              );
                            } else {
                              setFieldValue(
                                "had_asked_extensions_period_date.endDate",
                                date
                              );
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={Boolean(
                                touched.had_asked_extensions_period_date
                                  ?.endDate &&
                                  errors.had_asked_extensions_period_date
                                    ?.endDate
                              )}
                              helperText={
                                touched.had_asked_extensions_period_date
                                  ?.endDate &&
                                errors.had_asked_extensions_period_date?.endDate
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </FormControl>
                  </Stack>
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    I would like to apply for a research period extension 1
                    semester. That is... (you can choose only one option)
                  </Typography>
                  <TextField
                    select
                    value={values.how_long_extension_period}
                    onChange={(e) => handleChange(e)}
                    {...getFieldProps("how_long_extension_period")}
                    error={Boolean(
                      touched.how_long_extension_period &&
                        errors.how_long_extension_period
                    )}
                    helperText={
                      touched.how_long_extension_period &&
                      errors.how_long_extension_period
                    }
                  >
                    {howLongExtensionPeriodOption.map((item, index) => (
                      <MenuItem key={index} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    Please specify the date *
                  </Typography>
                  <Stack direction="row" spacing={3}>
                    <FormControl fullWidth>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          inputFormat="dd/MM/yyyy"
                          value={values.what_time_extend_period.startDate}
                          onChange={(date) => {
                            if (isValidDate(date)) {
                              setFieldValue(
                                "what_time_extend_period.startDate",
                                date.toISOString()
                              );
                            } else {
                              setFieldValue(
                                "what_time_extend_period.startDate",
                                date
                              );
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={Boolean(
                                touched.what_time_extend_period?.startDate &&
                                  errors.what_time_extend_period?.startDate
                              )}
                              helperText={
                                touched.what_time_extend_period?.startDate &&
                                errors.what_time_extend_period?.startDate
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </FormControl>

                    <Typography sx={{ paddingTop: 2 }}>to</Typography>

                    <FormControl fullWidth>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          inputFormat="dd/MM/yyyy"
                          value={values.what_time_extend_period.endDate}
                          onChange={(date) => {
                            if (isValidDate(date)) {
                              setFieldValue(
                                "what_time_extend_period.endDate",
                                date.toISOString()
                              );
                            } else {
                              setFieldValue(
                                "what_time_extend_period.endDate",
                                date
                              );
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={Boolean(
                                touched.what_time_extend_period?.endDate &&
                                  errors.what_time_extend_period?.endDate
                              )}
                              helperText={
                                touched.what_time_extend_period?.endDate &&
                                errors.what_time_extend_period?.endDate
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </FormControl>
                  </Stack>
                </FormControl>

                <FormGroup>
                  <FormControlLabel
                    disabled={paperUnSubmitted || !othersPleaseMention}
                    control={
                      <Checkbox
                        onChange={(e) => {
                          setFollowingResearchPaperSubmitted(e.target.checked);
                          setFieldValue("date_submission", null);
                        }}
                      />
                    }
                    label="Currently, my research paper has been submitted to the publisher."
                  />
                  {followingResearchPaperSubmitted && (
                    <>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={3}
                      >
                        <Typography sx={{ paddingTop: 2 }}>
                          Date of submission
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            inputFormat="dd/MM/yyyy"
                            value={values.date_submission}
                            onChange={(date) => {
                              if (isValidDate(date)) {
                                setFieldValue(
                                  "date_submission",
                                  date.toISOString()
                                );
                              } else {
                                setFieldValue("date_submission", date);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={Boolean(
                                  touched.date_submission &&
                                    errors.date_submission
                                )}
                                helperText={
                                  touched.date_submission &&
                                  errors.date_submission
                                }
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Stack>

                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={(e) => {
                              setManuscriptAvailable(e.target.checked);
                              setFieldValue("manuscript_available", null);
                            }}
                          />
                        }
                        label="Please attach a manuscript"
                        sx={{ paddingTop: 2 }}
                      />
                      {manuscriptAvailable && (
                        <TextField
                          inputProps={{
                            accept:
                              "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
                          }}
                          type="file"
                          onChange={(event) => {
                            setFieldValue(
                              `manuscript_available.file`,
                              event.currentTarget.files[0]
                            );
                          }}
                          error={Boolean(
                            touched.manuscript_available &&
                              errors.manuscript_available
                          )}
                          helperText={
                            touched.manuscript_available &&
                            errors.manuscript_available
                          }
                        />
                      )}
                    </>
                  )}
                  <FormControlLabel
                    disabled={
                      followingResearchPaperSubmitted || !othersPleaseMention
                    }
                    control={
                      <Checkbox
                        onChange={(e) => {
                          setPaperUnSubmitted(e.target.checked);
                          setFieldValue("mention_details", "");
                        }}
                      />
                    }
                    label="Currently, my research paper has not been submitted to the publisher."
                  />

                  {paperUnSubmitted && (
                    <Stack direction="row" spacing={3}>
                      <TextField
                        variant="outlined"
                        label="Please indicate the details"
                        sx={{ paddingBottom: 2 }}
                        {...getFieldProps("mention_details")}
                        error={Boolean(
                          touched.mention_details && errors.mention_details
                        )}
                        helperText={
                          touched.mention_details && errors.mention_details
                        }
                      />
                    </Stack>
                  )}

                  <FormControlLabel
                    disabled={
                      followingResearchPaperSubmitted || paperUnSubmitted
                    }
                    control={
                      <Checkbox
                        onChange={(e) => {
                          setOthersPleaseMention(!e.target.checked);
                          setFieldValue("others_please_mention", "");
                        }}
                      />
                    }
                    label={
                      <Stack direction="row" spacing={3}>
                        <Typography sx={{ paddingTop: 2 }}>Other</Typography>
                        <TextField
                          variant="outlined"
                          label="Please indicate"
                          disabled={othersPleaseMention}
                          {...getFieldProps("others_please_mention")}
                          error={Boolean(
                            touched.others_please_mention &&
                              errors.others_please_mention
                          )}
                          helperText={
                            touched.others_please_mention &&
                            errors.others_please_mention
                          }
                        />
                      </Stack>
                    }
                  />
                </FormGroup>

                <FormControlLabel
                  control={
                    <Checkbox
                      icon={<CircleUnchecked />}
                      checkedIcon={<CircleCheckedFilled />}
                      onChange={(e) => setDisableSubmit(!e.target.checked)}
                    />
                  }
                  label="My Mentor and I would like to apply for the research period extension without receiving monthly stipend nor
                  accommodation fee during the extension period. *"
                />

                <Stack
                  direction="row"
                  justifyContent="space-evenly"
                  alignItems="center"
                  spacing={12}
                  sx={{ paddingTop: 3 }}
                >
                  <Button
                    size="large"
                    variant="contained"
                    onClick={()=>{
                      navigate(`/extension-std/post_doc`, { replace: true })
                    }}
                    //href="/extension-std/post_doc"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="large"
                    type="submit"
                    variant="contained"
                    disabled={disableSubmit}
                    loading={isSubmitting.toString()}
                  >
                    Submit
                  </Button>
                </Stack>
              </Stack>
            </Form>
          </FormikProvider>
        </Grid>
      </Container>
    </Page>
  );
}
